import { useState, type MouseEvent, type SyntheticEvent } from 'react';
import { Alert, Snackbar, Tooltip } from '@mui/material';
import { useDispatch } from 'react-redux';
import { CustomDatePicker, DATE_PICKER_MODE } from '../../../../components/common/CustomDatePicker';
import axios from '../../../../services/api';
import { taskBaseUrl } from '../apiUrls';
import type { IProjectTaskModel } from '../models/ProjectTaskModel';
import { ProjectActions } from '../types/project';
import { useKanbanView } from '../contexts/KanbanViewContext';
import { extractErrorMessage } from '../../../../utils/errorUtils';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

type AddDueDateButtonProps = {
  task: IProjectTaskModel;
};

const stopPropagation = (event: SyntheticEvent) => {
  event.stopPropagation();
};

export const AddDueDateButton = ({ task }: AddDueDateButtonProps) => {
  const dispatch = useDispatch();
  const { refreshProject } = useKanbanView();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setSaveError(null);
    setIsPickerOpen(true);
    setAnchorEl(event.currentTarget);
  };

  const handleDateChange = async (selectedDate: Date | null) => {
    if (!selectedDate || isSaving) return;

    setIsSaving(true);
    try {
      const response = await axios.post(taskBaseUrl + 'ChangeTaskDurationAndPriority', {
        taskId: task.id,
        taskStart: task.taskStart ? new Date(task.taskStart).toISOString() : null,
        taskDueDate: selectedDate.toISOString(),
        priorityId: task.priorityId,
      });
      if (response.data?.Code !== 1) {
        throw new Error(
          response.data?.data?.Error || response.data?.Error || 'Failed to save the due date.'
        );
      }

      dispatch({
        type: ProjectActions.CHANGE_TASK_DURATION_AND_PRIORITY_SUCCESS,
        payload: response.data.data,
      });
      refreshProject();
      setAnchorEl(null);
      setIsPickerOpen(false);
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to save the due date.');
      console.error("[AddDueDateButton] Error changing task's due date:", message);
      setSaveError(message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Tooltip title="Add due date" arrow>
        <button
          type="button"
          className="kanban-card__add-due-date"
          aria-label="Add due date"
          onMouseDown={stopPropagation}
          onPointerDown={stopPropagation}
          onClick={handleOpen}
        >
          <CalendarTodayIcon sx={{ fontSize: 13 }} />
        </button>
      </Tooltip>
      {Boolean(anchorEl) && isPickerOpen && (
        <CustomDatePicker
          value={null}
          onChange={handleDateChange}
          mode={DATE_PICKER_MODE.DATETIME}
          openOnMount
          hideInput
          anchorEl={anchorEl}
          onClose={() => {
            setAnchorEl(null);
            setIsPickerOpen(false);
          }}
        />
      )}
      <Snackbar
        open={Boolean(saveError)}
        autoHideDuration={6000}
        onClose={() => setSaveError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      </Snackbar>
    </>
  );
};
